import { useMemo, useState } from "react";
import { topics } from "@/mocks/topics";
import { categories } from "@/mocks/categories";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import { formatCount, formatHeat } from "@/utils/format";
import type { Topic } from "@/pages/home/types";
import TopicModal from "@/pages/home/components/TopicModal";

const allTopics = topics as Topic[];

export default function TopicFeedSection() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [selected, setSelected] = useState<Topic | null>(null);

  const filtered = useMemo(() => {
    if (activeCategory === "all") return allTopics;
    return allTopics.filter((topic) => topic.category === activeCategory);
  }, [activeCategory]);

  const related = useMemo(() => {
    if (!selected) return [];
    return allTopics
      .filter((topic) => topic.id !== selected.id && topic.category === selected.category)
      .slice(0, 2);
  }, [selected]);

  return (
    <section id="topics" className="scroll-mt-20 bg-background-100 px-4 py-16 md:px-8 md:py-24">
      <Reveal>
        <SectionHeading
          eyebrow="Hot Topics"
          title="热门话题推荐，挑你关心的看"
          description="按分类筛选正在升温的话题，点开任意一张卡片，可以直接查看话题脉络、讨论规模与相关推荐。"
        />
      </Reveal>

      <Reveal delay={80}>
        <div className="mt-8 flex flex-wrap gap-2.5">
          {categories.map((category) => {
            const active = category.id === activeCategory;
            return (
              <button
                key={category.id}
                type="button"
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                <i className={category.icon} aria-hidden="true" />
                {category.name}
              </button>
            );
          })}
        </div>
      </Reveal>

      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((topic, index) => (
          <Reveal key={topic.id} delay={index * 60}>
            <button
              type="button"
              onClick={() => setSelected(topic)}
              className="group flex h-full w-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 text-left transition-all duration-300 hover:-translate-y-1 hover:border-primary-300"
            >
              <div className="relative h-44 w-full overflow-hidden">
                <img
                  src={topic.image}
                  alt={`${topic.title} 话题配图`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 rounded bg-foreground-950/60 px-2 py-0.5 text-[11px] font-medium text-background-50 backdrop-blur-sm">
                  {topic.categoryName}
                </span>
                <span className="absolute bottom-3 right-3 flex items-center gap-1 rounded bg-primary-500/95 px-2 py-0.5 text-[11px] font-bold text-background-50">
                  <i className="ri-fire-line" aria-hidden="true" />
                  {formatHeat(topic.heat)}
                </span>
              </div>

              <div className="flex flex-1 flex-col p-5">
                <div className="flex items-center justify-between text-[11px] text-foreground-500">
                  <span>{topic.author}</span>
                  <span>{topic.time}</span>
                </div>
                <h3 className="mt-2.5 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                  {topic.title}
                </h3>
                <p className="mt-2.5 line-clamp-3 flex-1 text-xs leading-relaxed text-foreground-600">
                  {topic.summary}
                </p>

                <div className="mt-4 flex flex-wrap gap-1.5">
                  {topic.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-800"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="mt-4 flex items-center gap-4 border-t border-background-200 pt-3 text-[11px] text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line" aria-hidden="true" />
                    {formatCount(topic.views)}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line" aria-hidden="true" />
                    {formatCount(topic.comments)}
                  </span>
                  <span className="ml-auto flex items-center gap-1 text-primary-600">
                    查看现场
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </span>
                </div>
              </div>
            </button>
          </Reveal>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
          <p className="text-sm text-foreground-600">该分类暂时没有正在升温的话题，换个分类看看？</p>
        </div>
      ) : null}

      <TopicModal
        topic={selected}
        related={related}
        onClose={() => setSelected(null)}
        onSelect={(topic) => setSelected(topic)}
      />
    </section>
  );
}